import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Eye, EyeOff, Upload, X, Image as ImageIcon } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import AdminLayout from '../../components/admin/AdminLayout';

const NewsManagement = () => {
  const [news, setNews] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    image_url: '',
    category: 'genel',
    published: true,
    created_at: '',
  });

  useEffect(() => {
    fetchNews();
  }, []);

  const fetchNews = async () => {
    try {
      const response = await fetch('http://localhost/api/news.php', {
        credentials: 'include',
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Sunucu hatası' }));
        toast.error(errorData.error || 'Haberler yüklenirken hata oluştu');
        return;
      }
      
      const text = await response.text();
      const data = text ? JSON.parse(text) : [];
      
      // is_published değerlerini normalize et
      const normalizedData = data.map(item => ({
        ...item,
        is_published: item.is_published === 1 || item.is_published === '1' || item.is_published === true ? 1 : 0,
      }));
      
      setNews(normalizedData);
    } catch (error) {
      console.error('Error fetching news:', error);
      toast.error('Haberler yüklenirken hata oluştu');
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const deleteOldImage = async (imageUrl) => {
    if (!imageUrl || !imageUrl.includes('/uploads/')) return;
    
    try {
      await fetch('http://localhost/api/delete-image.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ image_url: imageUrl }),
      });
    } catch (error) {
      // Sessizce hata yut (resim silinmese bile devam et)
      console.error('Error deleting old image:', error);
    }
  };

  const handleImageUpload = async (file) => {
    if (!file) return;

    // Dosya boyutu kontrolü (25MB)
    const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25MB
    if (file.size > MAX_FILE_SIZE) {
      toast.error('Dosya boyutu 25MB\'dan büyük olamaz');
      return;
    }

    // Dosya tipi kontrolü
    if (!file.type.startsWith('image/')) {
      toast.error('Lütfen geçerli bir resim dosyası seçin');
      return;
    }

    // Eski resmi sakla (yeni resim yüklendikten sonra silmek için)
    const oldImageUrl = formData.image_url;

    try {
      const formDataUpload = new FormData();
      formDataUpload.append('image', file);

      const response = await fetch('http://localhost/api/upload.php', {
        method: 'POST',
        credentials: 'include',
        body: formDataUpload,
      });

      const text = await response.text();
      let data = {};
      try {
        data = text ? JSON.parse(text) : {};
      } catch (e) {
        console.error('Error parsing response:', e);
        console.error('Response text:', text);
        toast.error('Sunucu yanıtı işlenemedi');
        return;
      }

      if (!response.ok) {
        toast.error(data.error || 'Görsel yüklenirken hata oluştu');
        return;
      }

      // URL'yi kontrol et ve düzelt
      let imageUrl = data.url || data.image_url || '';

      // Eğer sadece filename varsa, tam URL oluştur
      if (!imageUrl && data.filename) {
        imageUrl = `/uploads/${data.filename}`;
      }

      // Relative path'i tam URL'ye çevir
      if (imageUrl && !imageUrl.startsWith('http')) {
        if (imageUrl.startsWith('/uploads/')) {
          imageUrl = `http://localhost${imageUrl}`;
        } else if (imageUrl.startsWith('/')) {
          imageUrl = `http://localhost${imageUrl}`;
        } else {
          imageUrl = `http://localhost/uploads/${imageUrl}`;
        }
      }

      if (imageUrl) {
        // Yeni resim başarıyla yüklendi, eski resmi sil
        if (oldImageUrl && oldImageUrl !== imageUrl && oldImageUrl.includes('/uploads/')) {
          await deleteOldImage(oldImageUrl);
        }
        
        setFormData(prev => ({ ...prev, image_url: imageUrl }));
        toast.success('Görsel başarıyla yüklendi');
      } else {
        console.error('No image URL found in response:', data);
        toast.error('Görsel URL alınamadı');
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Görsel yüklenirken hata oluştu: ' + (error.message || 'Bilinmeyen hata'));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const url = editingItem
        ? `http://localhost/api/news.php?id=${editingItem.id}`
        : 'http://localhost/api/news.php';
      
      const method = editingItem ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          title: formData.title,
          content: formData.content,
          image_url: formData.image_url,
          is_published: formData.published ? 1 : 0,
          created_at: formData.created_at && formData.created_at.trim() !== '' ? formData.created_at.trim() : null,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Sunucu hatası' }));
        toast.error(errorData.error || 'Haber kaydedilirken hata oluştu');
        return;
      }

      const data = await response.json();
      toast.success(editingItem ? 'Haber başarıyla güncellendi' : 'Haber başarıyla eklendi');
      setIsModalOpen(false);
      setEditingItem(null);
      setFormData({
        title: '',
        content: '',
        image_url: '',
        category: 'genel',
        published: true,
        created_at: '',
      });
      fetchNews();
    } catch (error) {
      console.error('Error saving news:', error);
      toast.error('Haber kaydedilirken hata oluştu');
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    // Tarihi formatla (YYYY-MM-DD formatında)
    let formattedDate = '';
    if (item.created_at) {
      try {
        // Eğer zaten YYYY-MM-DD formatındaysa direkt kullan
        if (/^\d{4}-\d{2}-\d{2}$/.test(item.created_at)) {
          formattedDate = item.created_at;
        } else {
          // Değilse Date objesine çevir
          const date = new Date(item.created_at);
          // Geçerli bir tarih mi kontrol et
          if (!isNaN(date.getTime())) {
            formattedDate = date.toISOString().slice(0, 10); // YYYY-MM-DD formatı
          }
        }
      } catch (error) {
        console.error('Error parsing date:', error);
        formattedDate = '';
      }
    }
    setFormData({
      title: item.title,
      content: item.content,
      image_url: item.image_url || '',
      category: 'genel',
      published: item.is_published === 1 || item.is_published === '1' || item.is_published === true,
      created_at: formattedDate,
    });
    setIsModalOpen(true);
  };

  const handleDelete = (id) => {
    setDeleteItemId(id);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleteItemId) return;

    try {
      const response = await fetch(`http://localhost/api/news.php?id=${deleteItemId}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Sunucu hatası' }));
        toast.error(errorData.error || 'Haber silinirken hata oluştu');
        return;
      }

      const data = await response.json();
      toast.success('Haber başarıyla silindi');
      setIsDeleteModalOpen(false);
      setDeleteItemId(null);
      fetchNews();
    } catch (error) {
      console.error('Error deleting news:', error);
      toast.error('Haber silinirken hata oluştu');
    }
  };

  const togglePublished = async (item) => {
    // Mevcut durumu kontrol et - farklı formatları destekle
    const currentPublished = item.is_published === 1 || item.is_published === '1' || item.is_published === true;
    const newPublishedValue = currentPublished ? 0 : 1;
    
    // State'i anında güncelle (optimistic update)
    setNews(prev => prev.map(newsItem => 
      newsItem.id === item.id 
        ? { ...newsItem, is_published: newPublishedValue }
        : newsItem
    ));
    
    try {
      const response = await fetch(`http://localhost/api/news.php?id=${item.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          title: item.title,
          content: item.content,
          image_url: item.image_url || '',
          is_published: newPublishedValue,
        }),
      });

      if (!response.ok) {
        // Hata durumunda state'i geri al
        setNews(prev => prev.map(newsItem => 
          newsItem.id === item.id 
            ? { ...newsItem, is_published: currentPublished ? 1 : 0 }
            : newsItem
        ));
        const errorData = await response.json().catch(() => ({ error: 'Sunucu hatası' }));
        toast.error(errorData.error || 'Güncelleme sırasında hata oluştu');
        return;
      }

      toast.success('Haber durumu güncellendi');
      // Verileri yeniden çek
      fetchNews();
    } catch (error) {
      // Hata durumunda state'i geri al
      setNews(prev => prev.map(newsItem => 
        newsItem.id === item.id 
          ? { ...newsItem, is_published: currentPublished ? 1 : 0 }
          : newsItem
      ));
      console.error('Error toggling published:', error);
      toast.error('Güncelleme sırasında hata oluştu');
    }
  };

  const openAddModal = () => {
    setEditingItem(null);
    // Yeni haber için şu anki tarihi varsayılan olarak ayarla
    const now = new Date();
    const formattedDate = now.toISOString().slice(0, 10); // YYYY-MM-DD formatı
    setFormData({
      title: '',
      content: '',
      image_url: '',
      category: 'genel',
      published: true,
      created_at: formattedDate,
    });
    setIsModalOpen(true);
  };

  return (
    <AdminLayout
      addButton={
        <Button
          onClick={openAddModal}
          data-testid="add-news-btn"
          className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white flex items-center gap-2"
        >
          <Plus size={20} />
          Yeni Haber
        </Button>
      }
    >
      <div>
        <div className="mt-6">
        {news.length === 0 ? (
          <div className="text-center py-20">
            <div className="text-6xl mb-4">📰</div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">Henüz haber yok</h3>
            <p className="text-gray-600 mb-6">Yeni bir haber ekleyerek başlayın</p>
            <Button onClick={openAddModal} className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700">
              İlk Haberi Ekle
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {news.map((item) => (
              <div
                key={item.id}
                data-testid={`news-card-${item.id}`}
                className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-xl transition-all"
              >
                {item.image_url && (
                  <img
                    src={item.image_url}
                    alt={item.title}
                    className="w-full h-48 object-cover"
                  />
                )}
                <div className="p-6">
                  <div className="flex flex-wrap items-center gap-2 mb-3">
                    {item.created_at && (
                      <span className="px-3 py-1 bg-purple-100 text-purple-800 text-xs font-semibold rounded-full">
                        {new Date(item.created_at).toLocaleDateString('tr-TR', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </span>
                    )}
                    {item.is_published ? (
                      <span className="px-3 py-1 bg-green-100 text-green-800 text-xs font-semibold rounded-full flex items-center gap-1">
                        <Eye size={12} />
                        Yayında
                      </span>
                    ) : (
                      <span className="px-3 py-1 bg-gray-100 text-gray-800 text-xs font-semibold rounded-full flex items-center gap-1">
                        <EyeOff size={12} />
                        Taslak
                      </span>
                    )}
                  </div>
                  <h3 className="text-base sm:text-xl font-bold text-gray-900 mb-2 line-clamp-2">{item.title}</h3>
                  <p className="text-gray-600 text-xs sm:text-sm mb-4 line-clamp-3">{item.content}</p>
                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                    <Button
                      onClick={() => handleEdit(item)}
                      data-testid={`edit-news-${item.id}`}
                      variant="outline"
                      size="sm"
                      className="flex-1 flex items-center justify-center gap-1 py-3 sm:py-1.5 text-sm sm:text-xs"
                    >
                      <Edit size={18} className="sm:w-4 sm:h-4" />
                      Düzenle
                    </Button>
                    <Button
                      onClick={() => togglePublished(item)}
                      data-testid={`toggle-news-${item.id}`}
                      variant="outline"
                      size="sm"
                      className="py-3 sm:py-1.5"
                    >
                      {item.is_published ? <EyeOff size={18} className="sm:w-4 sm:h-4" /> : <Eye size={18} className="sm:w-4 sm:h-4" />}
                    </Button>
                    <Button
                      onClick={() => handleDelete(item.id)}
                      data-testid={`delete-news-${item.id}`}
                      variant="destructive"
                      size="sm"
                      className="py-3 sm:py-1.5"
                    >
                      <Trash2 size={18} className="sm:w-4 sm:h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        </div>
      </div>

      {/* Add/Edit Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto w-[95vw] sm:w-full mx-2 sm:mx-auto">
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Haberi Düzenle' : 'Yeni Haber Ekle'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Başlık *</label>
              <Input
                data-testid="news-title-input"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="Haber başlığı"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">İçerik *</label>
              <Textarea
                data-testid="news-content-input"
                name="content"
                value={formData.content}
                onChange={handleInputChange}
                placeholder="Haber içeriği"
                rows={6}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Tarih</label>
              <Input
                type="date"
                name="created_at"
                value={formData.created_at}
                onChange={handleInputChange}
                className="w-full"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Görsel Yükle</label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Sol: Yükleme Alanı */}
                <div
                  className="relative border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-500 hover:bg-blue-50/50 transition-all cursor-pointer group"
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    e.currentTarget.classList.add('border-blue-500', 'bg-blue-50/50');
                  }}
                  onDragLeave={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    e.currentTarget.classList.remove('border-blue-500', 'bg-blue-50/50');
                  }}
                  onDrop={async (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    e.currentTarget.classList.remove('border-blue-500', 'bg-blue-50/50');
                    const file = e.dataTransfer.files?.[0];
                    if (file && file.type.startsWith('image/')) {
                      await handleImageUpload(file);
                    } else {
                      toast.error('Lütfen geçerli bir resim dosyası seçin');
                    }
                  }}
                  onClick={() => {
                    const input = document.getElementById('news-image-upload-input');
                    input?.click();
                  }}
                >
                  <input
                    id="news-image-upload-input"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        await handleImageUpload(file);
                      }
                    }}
                  />
                  {!formData.image_url ? (
                    <div className="flex flex-col items-center gap-3">
                      <div className="w-16 h-16 rounded-full bg-gray-100 group-hover:bg-blue-100 flex items-center justify-center transition-colors">
                        <Upload className="w-8 h-8 text-gray-400 group-hover:text-blue-600 transition-colors" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-700 mb-1">
                          Görsel sürükle ve bırak
                        </p>
                        <p className="text-xs text-gray-500">
                          veya tıklayarak seç
                        </p>
                      </div>
                      <p className="text-xs text-gray-400">
                        PNG, JPG, GIF (max. 25MB)
                      </p>
                    </div>
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center bg-white/80 backdrop-blur-sm rounded-lg">
                      <div className="text-center">
                        <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                        <p className="text-xs text-gray-500">Görseli değiştirmek için tıklayın</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Sağ: Önizleme */}
                <div className="border-2 border-gray-200 rounded-lg p-4 bg-gray-50 flex items-center justify-center min-h-[200px]">
                  {formData.image_url ? (
                    <div className="relative w-full h-full flex items-center justify-center">
                      <img 
                        key={formData.image_url}
                        src={formData.image_url} 
                        alt="Önizleme" 
                        className="max-w-full max-h-48 object-contain rounded-lg"
                        onError={(e) => {
                          console.error('Image load error for URL:', formData.image_url);
                          e.target.style.display = 'none';
                          const errorDiv = e.target.parentElement.querySelector('.image-error');
                          if (errorDiv) {
                            errorDiv.style.display = 'block';
                          }
                        }}
                        onLoad={() => {
                          console.log('Image loaded successfully:', formData.image_url);
                        }}
                      />
                      <div className="image-error hidden text-center text-red-500 text-xs">
                        <p>Görsel yüklenemedi</p>
                        <p className="text-gray-400 break-all text-[10px] mt-1">{formData.image_url}</p>
                      </div>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setFormData(prev => ({ ...prev, image_url: '' }));
                          const input = document.getElementById('news-image-upload-input');
                          if (input) input.value = '';
                        }}
                        className="absolute -top-2 -right-2 w-6 h-6 bg-red-600 text-white rounded-full flex items-center justify-center hover:bg-red-700 transition-colors z-10"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="text-center text-gray-400">
                      <ImageIcon className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p className="text-xs">Önizleme</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-center">
              <input
                data-testid="news-published-checkbox"
                type="checkbox"
                name="published"
                checked={formData.published}
                onChange={handleInputChange}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <label className="ml-2 text-sm font-medium text-gray-700">Hemen yayınla</label>
            </div>

            <div className="flex gap-3">
              <Button
                type="submit"
                data-testid="save-news-btn"
                className="flex-1 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700"
              >
                {editingItem ? 'Güncelle' : 'Ekle'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsModalOpen(false)}
                data-testid="cancel-news-btn"
              >
                İptal
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={isDeleteModalOpen} onOpenChange={setIsDeleteModalOpen}>
        <DialogContent className="max-w-md w-[95vw] sm:w-full mx-2 sm:mx-auto">
          <DialogHeader>
            <DialogTitle>Silme Onayı</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-gray-700">Bu haberi silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setIsDeleteModalOpen(false);
                setDeleteItemId(null);
              }}
            >
              İptal
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={confirmDelete}
            >
              Sil
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default NewsManagement;